import { useEffect } from 'react';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';

import { useProject } from '@contexts/Project.context';
import { getLayerByCustomId, deleteLayerByCustomId } from '@utils/map/helpers';

/**
 * Finds the map overlays whose element carries a class name.
 *
 * Measurement labels are overlays on the map rather than features in the layer,
 * so anything acting on the measurements as a whole has to reach them here:
 * neither the layer's source nor its visibility touches them.
 *
 * @param {Object} options The options for finding overlays.
 * @param {Object} options.mapObject The map object to search.
 * @param {string} options.className The class name to match on the overlay's element.
 * @returns {Array} The matching overlays.
 */
const overlaysWithElementClassName = ({ mapObject, className }) =>
	mapObject
		.getOverlays()
		.getArray()
		.filter(overlay => overlay?.getElement()?.className?.includes(className));

/**
 * Removes overlays with a specific element class name from a map object.
 *
 * @param {Object} options The options for removing overlays.
 * @param {Object} options.mapObject The map object from which to remove overlays.
 * @param {string} options.className The class name of the element of the overlay to be removed.
 * @returns {void}
 */
const removeOverlaysWithElementClassName = ({ mapObject, className }) => {
	overlaysWithElementClassName({
		mapObject: mapObject,
		className: className,
	}).forEach(overlay => mapObject.removeOverlay(overlay));
};

/**
 * MeasurementLayer — owns the temporary vector layer that measurements are
 * drawn into.
 *
 * Renders nothing: its whole job is the layer's lifecycle, expressed as mount
 * and unmount. Mounting it creates the layer and publishes it as
 * `measurementLayer`; unmounting removes the layer, its overlays and that
 * reference. Callers therefore add and remove measurements by rendering this
 * component conditionally, and never have to clean up by hand.
 *
 * The layer is deliberately not persisted — everything drawn is lost on unmount.
 * Hiding is therefore separate from unmounting: pass `visible={false}` to take
 * the measurements off screen while keeping them, which is what anything that
 * takes the map over needs (annotation, a boundary edit, an area draw) —
 * unmounting there would silently discard the user's sketches.
 *
 * @param {Object} props
 * @param {string} [props.layerId] Custom layer id used to find and remove the layer.
 * @param {boolean} [props.visible] Whether the measurements are shown. Hidden, not destroyed.
 * @returns {null} Nothing is rendered.
 */
const MeasurementLayer = ({ layerId = 'measurementLayer', visible = true }) => {
	const { mapObject, dispatch } = useProject();

	useEffect(() => {
		if (!mapObject) return;

		let layer = getLayerByCustomId(mapObject, layerId);

		if (!layer) {
			layer = new VectorLayer({
				zIndex: 30,
				source: new VectorSource(),
				name: 'Measurements',
				properties: {
					customLayerId: layerId,
				},

				// Set style based on feature type
				style: feature => feature.get('style'),
			});
			mapObject.addLayer(layer);
		} else {
			layer.getSource().clear();
		}

		dispatch({ type: 'setMeasurementLayer', payload: layer });

		// Cleanup
		return () => {
			removeOverlaysWithElementClassName({
				mapObject: mapObject,
				className: `${layerId}Overlay`,
			});

			deleteLayerByCustomId(mapObject, layerId);
			dispatch({ type: 'setMeasurementLayer', payload: null });
		};
	}, [mapObject, layerId, dispatch]);

	// Visibility is its own effect so that toggling it never re-runs the effect
	// above — that one owns the layer's existence, and re-running it would clear
	// the source. The labels are overlays, which setVisible does not reach, so
	// they are hidden element by element.
	//
	// `visibility`, not `display`: OpenLayers owns the `display` of an overlay's
	// element (Overlay#setVisible) and caches what it last wrote, so a value set
	// here is silently reverted the first time the overlay leaves and re-enters
	// the viewport — which every view change during annotation mode causes.
	// Nothing in OpenLayers touches `visibility`.
	useEffect(() => {
		if (!mapObject) return;

		getLayerByCustomId(mapObject, layerId)?.setVisible(visible);

		overlaysWithElementClassName({
			mapObject: mapObject,
			className: `${layerId}Overlay`,
		}).forEach(overlay => {
			overlay.getElement().style.visibility = visible ? '' : 'hidden';
		});
	}, [mapObject, layerId, visible]);

	return null;
};

export default MeasurementLayer;
